import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "displayName",
    "formatType",
    "formatOptions",
    "currencySymbol",
    "decimals",
    "dateFormat",
    "sortOrder",
    "groupBy",
    "columnWidth",
    "alignment"
  ]

  static values = {
    fieldName: String,
    fieldType: String,
    configuration: Object
  }

  connect() {
    this.initializeConfiguration()
    this.updateFormatOptions()
  }

  initializeConfiguration() {
    if (!this.configurationValue) {
      this.configurationValue = {
        display_name: this.fieldNameValue,
        formatting: this.getDefaultFormatting(),
        sorting: { enabled: false, order: 'asc' },
        grouping: { enabled: false },
        display: {
          width: 'auto',
          alignment: 'left'
        }
      }
    }
    this.populateForm()
  }

  getDefaultFormatting() {
    switch (this.fieldTypeValue) {
      case 'currency':
        return { type: 'currency', symbol: '$', decimals: 2 }
      case 'number':
        return { type: 'number', decimals: 0 }
      case 'date':
        return { type: 'date', format: 'MM/DD/YYYY' }
      case 'datetime':
        return { type: 'datetime', format: 'MM/DD/YYYY HH:mm' }
      case 'boolean':
        return { type: 'boolean', trueValue: 'Yes', falseValue: 'No' }
      default:
        return { type: 'text' }
    }
  }

  populateForm() {
    // Set display name
    if (this.hasDisplayNameTarget) {
      this.displayNameTarget.value = this.configurationValue.display_name || this.fieldNameValue
    }

    // Set format type
    if (this.hasFormatTypeTarget) {
      this.formatTypeTarget.value = this.configurationValue.formatting.type
    }

    // Set format-specific options
    this.populateFormatOptions()

    // Set sorting options
    if (this.hasSortOrderTarget) {
      this.sortOrderTarget.value = this.configurationValue.sorting.order || 'asc'
    }

    // Set grouping options
    if (this.hasGroupByTarget) {
      this.groupByTarget.checked = this.configurationValue.grouping.enabled || false
    }

    // Set display options
    if (this.hasColumnWidthTarget) {
      this.columnWidthTarget.value = this.configurationValue.display.width || 'auto'
    }

    if (this.hasAlignmentTarget) {
      this.alignmentTarget.value = this.configurationValue.display.alignment || 'left'
    }
  }

  populateFormatOptions() {
    const formatting = this.configurationValue.formatting

    if (this.hasCurrencySymbolTarget && formatting.symbol) {
      this.currencySymbolTarget.value = formatting.symbol
    }

    if (this.hasDecimalsTarget && formatting.decimals !== undefined) {
      this.decimalsTarget.value = formatting.decimals
    }

    if (this.hasDateFormatTarget && formatting.format) {
      this.dateFormatTarget.value = formatting.format
    }
  }

  updateDisplayName(event) {
    this.configurationValue.display_name = event.target.value
    this.notifyConfigurationChange()
  }

  updateFormatType(event) {
    const newType = event.target.value
    this.configurationValue.formatting = {
      type: newType,
      ...this.getDefaultFormattingForType(newType)
    }
    this.updateFormatOptions()
    this.notifyConfigurationChange()
  }

  getDefaultFormattingForType(type) {
    switch (type) {
      case 'currency':
        return { symbol: '$', decimals: 2 }
      case 'number':
        return { decimals: 0 }
      case 'date':
        return { format: 'MM/DD/YYYY' }
      case 'datetime':
        return { format: 'MM/DD/YYYY HH:mm' }
      case 'boolean':
        return { trueValue: 'Yes', falseValue: 'No' }
      default:
        return {}
    }
  }

  updateFormatOptions() {
    if (!this.hasFormatOptionsTarget) return

    const formatType = this.configurationValue.formatting.type
    let optionsHtml = ''

    switch (formatType) {
      case 'currency':
        optionsHtml = this.getCurrencyOptions()
        break
      case 'number':
        optionsHtml = this.getNumberOptions()
        break
      case 'date':
      case 'datetime':
        optionsHtml = this.getDateOptions()
        break
      case 'boolean':
        optionsHtml = this.getBooleanOptions()
        break
      default:
        optionsHtml = '<p class="text-muted">No additional formatting options available</p>'
    }

    this.formatOptionsTarget.innerHTML = optionsHtml
  }

  getCurrencyOptions() {
    const formatting = this.configurationValue.formatting
    return `
      <div class="row">
        <div class="col-md-6">
          <label class="form-label">Currency Symbol</label>
          <select class="form-select" 
                  data-field-configuration-target="currencySymbol"
                  data-action="change->field-configuration#updateCurrencySymbol">
            <option value="$" ${formatting.symbol === '$' ? 'selected' : ''}>$ (Dollar)</option>
            <option value="€" ${formatting.symbol === '€' ? 'selected' : ''}>€ (Euro)</option>
            <option value="£" ${formatting.symbol === '£' ? 'selected' : ''}>£ (Pound)</option>
            <option value="¥" ${formatting.symbol === '¥' ? 'selected' : ''}>¥ (Yen)</option>
          </select>
        </div>
        <div class="col-md-6">
          <label class="form-label">Decimal Places</label>
          <input type="number" 
                 class="form-control" 
                 min="0" 
                 max="4" 
                 value="${formatting.decimals || 2}"
                 data-field-configuration-target="decimals"
                 data-action="change->field-configuration#updateDecimals">
        </div>
      </div>
    `
  }

  getNumberOptions() {
    const formatting = this.configurationValue.formatting
    return `
      <div class="row">
        <div class="col-md-6">
          <label class="form-label">Decimal Places</label>
          <input type="number" 
                 class="form-control" 
                 min="0" 
                 max="10" 
                 value="${formatting.decimals || 0}"
                 data-field-configuration-target="decimals"
                 data-action="change->field-configuration#updateDecimals">
        </div>
        <div class="col-md-6">
          <label class="form-label">Thousands Separator</label>
          <select class="form-select" 
                  data-action="change->field-configuration#updateThousandsSeparator">
            <option value="," ${formatting.thousandsSeparator === ',' ? 'selected' : ''}>Comma (1,000)</option>
            <option value=" " ${formatting.thousandsSeparator === ' ' ? 'selected' : ''}>Space (1 000)</option>
            <option value="" ${!formatting.thousandsSeparator ? 'selected' : ''}>None (1000)</option>
          </select>
        </div>
      </div>
    `
  }

  getDateOptions() {
    const formatting = this.configurationValue.formatting
    const isDateTime = formatting.type === 'datetime'
    
    return `
      <div class="row">
        <div class="col-md-12">
          <label class="form-label">Date Format</label>
          <select class="form-select" 
                  data-field-configuration-target="dateFormat"
                  data-action="change->field-configuration#updateDateFormat">
            <option value="MM/DD/YYYY" ${formatting.format === 'MM/DD/YYYY' ? 'selected' : ''}>MM/DD/YYYY</option>
            <option value="DD/MM/YYYY" ${formatting.format === 'DD/MM/YYYY' ? 'selected' : ''}>DD/MM/YYYY</option>
            <option value="YYYY-MM-DD" ${formatting.format === 'YYYY-MM-DD' ? 'selected' : ''}>YYYY-MM-DD</option>
            <option value="MMM DD, YYYY" ${formatting.format === 'MMM DD, YYYY' ? 'selected' : ''}>MMM DD, YYYY</option>
            ${isDateTime ? `
              <option value="MM/DD/YYYY HH:mm" ${formatting.format === 'MM/DD/YYYY HH:mm' ? 'selected' : ''}>MM/DD/YYYY HH:mm</option>
              <option value="DD/MM/YYYY HH:mm" ${formatting.format === 'DD/MM/YYYY HH:mm' ? 'selected' : ''}>DD/MM/YYYY HH:mm</option>
              <option value="YYYY-MM-DD HH:mm" ${formatting.format === 'YYYY-MM-DD HH:mm' ? 'selected' : ''}>YYYY-MM-DD HH:mm</option>
            ` : ''}
          </select>
        </div>
      </div>
    `
  }

  getBooleanOptions() {
    const formatting = this.configurationValue.formatting
    return `
      <div class="row">
        <div class="col-md-6">
          <label class="form-label">True Value Display</label>
          <input type="text" 
                 class="form-control" 
                 value="${formatting.trueValue || 'Yes'}"
                 data-action="change->field-configuration#updateTrueValue">
        </div>
        <div class="col-md-6">
          <label class="form-label">False Value Display</label>
          <input type="text" 
                 class="form-control" 
                 value="${formatting.falseValue || 'No'}"
                 data-action="change->field-configuration#updateFalseValue">
        </div>
      </div>
    `
  }

  // Format option update methods
  updateCurrencySymbol(event) {
    this.configurationValue.formatting.symbol = event.target.value
    this.notifyConfigurationChange()
  }

  updateDecimals(event) {
    this.configurationValue.formatting.decimals = parseInt(event.target.value)
    this.notifyConfigurationChange()
  }

  updateThousandsSeparator(event) {
    this.configurationValue.formatting.thousandsSeparator = event.target.value
    this.notifyConfigurationChange()
  }

  updateDateFormat(event) {
    this.configurationValue.formatting.format = event.target.value
    this.notifyConfigurationChange()
  }

  updateTrueValue(event) {
    this.configurationValue.formatting.trueValue = event.target.value
    this.notifyConfigurationChange()
  }

  updateFalseValue(event) {
    this.configurationValue.formatting.falseValue = event.target.value
    this.notifyConfigurationChange()
  }

  // Sorting and grouping methods
  updateSortOrder(event) {
    this.configurationValue.sorting.order = event.target.value
    this.configurationValue.sorting.enabled = true
    this.notifyConfigurationChange()
  }

  toggleGroupBy(event) {
    this.configurationValue.grouping.enabled = event.target.checked
    this.notifyConfigurationChange()
  }

  // Display options methods
  updateColumnWidth(event) {
    this.configurationValue.display.width = event.target.value
    this.notifyConfigurationChange()
  }

  updateAlignment(event) {
    this.configurationValue.display.alignment = event.target.value
    this.notifyConfigurationChange()
  }

  // Utility methods
  notifyConfigurationChange() {
    // Dispatch custom event to notify parent controller
    const event = new CustomEvent('field-configuration-changed', {
      detail: {
        fieldName: this.fieldNameValue,
        configuration: this.configurationValue
      },
      bubbles: true
    })
    this.element.dispatchEvent(event)
  }

  getConfiguration() {
    return this.configurationValue
  }

  resetToDefaults() {
    this.configurationValue = {
      display_name: this.fieldNameValue,
      formatting: this.getDefaultFormatting(),
      sorting: { enabled: false, order: 'asc' },
      grouping: { enabled: false },
      display: {
        width: 'auto',
        alignment: 'left'
      }
    }
    this.populateForm()
    this.updateFormatOptions()
    this.notifyConfigurationChange()
  }

  saveConfiguration() {
    // This method is called when the user clicks "Apply Configuration"
    // The configuration is already being tracked via notifyConfigurationChange
    // So we just need to ensure the modal closes properly
  }
}